import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from '@nextui-org/react';
import {
  deleteSettlement,
  deleteSettlementAudited,
  getClaimsBlockingSettlementDeletion,
} from 'common/src/api/settlements';
import { Settlement } from 'common/src/utils/types';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useUserStore from '../../../../../common/src/stores/useUserStore';

interface DeleteSettlementModalProps {
  isOpen: boolean;
  onClose: () => void;
  settlement: Settlement | null;
  onSuccess: () => void;
}

const DeleteSettlementModal = ({
  isOpen,
  onClose,
  settlement,
  onSuccess,
}: DeleteSettlementModalProps) => {
  const { session } = useUserStore();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCheckingClaims, setIsCheckingClaims] = useState(false);
  const [blockingClaims, setBlockingClaims] = useState<{ id: number }[]>([]);

  useEffect(() => {
    if (isOpen && settlement) {
      checkBlockingClaims();
    } else {
      setBlockingClaims([]);
    }
  }, [isOpen, settlement]);

  const checkBlockingClaims = async () => {
    if (!settlement) return;
    setIsCheckingClaims(true);
    try {
      const claims = await getClaimsBlockingSettlementDeletion(settlement.id);
      setBlockingClaims(claims);
    } catch (error) {
      console.error('Error checking blocking claims:', error);
    } finally {
      setIsCheckingClaims(false);
    }
  };

  const handleDelete = async () => {
    if (!settlement) return;

    try {
      setIsDeleting(true);
      const success = session?.access_token
        ? await deleteSettlementAudited(settlement.id, session.access_token)
        : await deleteSettlement(settlement.id);
      if (success) {
        toast.success('Liquidación eliminada correctamente');
        onSuccess();
        onClose();
      } else {
        toast.error('Error al eliminar la liquidación');
      }
    } catch (error) {
      toast.error('Error al eliminar la liquidación');
      console.error('Error deleting settlement:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const isBlocked = blockingClaims.length > 0;

  const formatClaimList = () => {
    return blockingClaims.map((c) => `#${c.id}`).join(', ');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>
          {isBlocked ? 'No se puede eliminar' : 'Confirmar eliminación'}
        </ModalHeader>
        <ModalBody>
          {isCheckingClaims ? (
            <div className='flex items-center gap-2'>
              <Spinner size='sm' />
              <span>Verificando dependencias...</span>
            </div>
          ) : isBlocked ? (
            <div className='flex flex-col gap-2'>
              <p>
                No puedes borrar esta liquidación porque las compensaciones{' '}
                <strong>{formatClaimList()}</strong> la consideran.
              </p>
              <p>
                Debes borrar primero esas compensaciones para proceder.
              </p>
            </div>
          ) : (
            <>
              <p>
                ¿Está seguro que desea eliminar la liquidación{' '}
                <strong>{settlement?.reference_number}</strong>?
              </p>
              <p className='text-danger'>Esta acción no se puede deshacer.</p>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant='light' onPress={onClose}>
            {isBlocked ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!isBlocked && !isCheckingClaims && (
            <Button
              color='danger'
              onPress={handleDelete}
              isLoading={isDeleting}
            >
              Eliminar
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteSettlementModal;
